import React, { useCallback, useEffect, useRef, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrderMobileCardShell from "./OrderMobileCardShell";
import OrdersSubTabs from "./OrdersSubTabs";
import AuthService from "../../../api/services/AuthService";
import {
  MARGIN_SUB_TABS,
  MARGIN_POSITION_SIDE_OPTIONS,
  buildMarginPairOptions,
} from "./marginOrderFilterConstants";
import OrderTablePagination, { useOrderTablePagination } from "./OrderTablePagination";

const ORDER_DESKTOP_COL_COUNT = 12;

function dec(v) {
  if (v == null) return null;
  if (typeof v === "object" && v.$numberDecimal != null) return v.$numberDecimal;
  return v;
}

function fmt4(v) {
  const n = parseFloat(dec(v) || 0);
  return Number.isFinite(n) ? n.toFixed(4) : "—";
}

function fmtDt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`,
  };
}

function formatPairLabel(raw) {
  if (!raw) return "—";
  const s = String(raw);
  if (s.includes("/")) return s;
  if (s.length > 4) return `${s.slice(0, -4)}/${s.slice(-4)}`;
  return s;
}

function splitPairAssets(raw) {
  const s = String(raw || "");
  if (s.includes("/")) {
    const [base, quote] = s.split("/");
    return { base: base || "", quote: quote || "" };
  }
  if (s.length > 4) return { base: s.slice(0, -4), quote: s.slice(-4) };
  return { base: "", quote: "" };
}

function isLiquidatedPos(pos) {
  const reason = String(pos?.close_reason || "").toUpperCase();
  const status = String(pos?.status || "").toUpperCase();
  return reason === "LIQUIDATION" || reason === "LIQUIDATED" || status === "LIQUIDATED";
}

function posStatusLabel(pos) {
  if (isLiquidatedPos(pos)) return "Liquidated";
  const statusMap = { CLOSED: "Close All", LIQUIDATED: "Liquidated", OPEN: "Open Position" };
  return statusMap[pos?.status] || pos?.status || "—";
}

function normalizeMarginPositionHistoryItem(raw) {
  if (!raw) return raw;
  return {
    ...raw,
    quantity: dec(raw.quantity),
    entry_price: dec(raw.entry_price),
    close_price: dec(raw.close_price),
    notional: dec(raw.notional),
    liquidation_price: dec(raw.liquidation_price),
    realized_pnl: dec(raw.realized_pnl),
    total_fees: dec(raw.total_fees),
    liq_fee: dec(raw.liq_fee),
    total_interest_charged: dec(raw.total_interest_charged),
  };
}

function DatePickerCell({ value, onChange, ariaLabel, inputRef, placeholder = "From" }) {
  return (
    <div className="so-dateCell">
      <span className="so-dateCell__text" aria-hidden="true">{value || placeholder}</span>
      <input
        ref={inputRef}
        type="date"
        className="so-dateCell__native"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
      />
    </div>
  );
}

const MarginPositionHistory = () => {
  // Match margin trade page: no date/side filters until the user sets them.
  const [side, setSide] = useState("all");
  const [contract, setContract] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pairOptions, setPairOptions] = useState([{ value: "all", label: "All" }]);
  const [rawAccounts, setRawAccounts] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);

  const dateFromRefDesk = useRef(null);
  const dateToRefDesk = useRef(null);
  const dateFromRefMob = useRef(null);
  const dateToRefMob = useRef(null);

  useEffect(() => {
    AuthService.marginAccounts()
      .then((res) => {
        if (res?.success) {
          setRawAccounts(res.data || []);
          setPairOptions(buildMarginPairOptions(res.data || []));
        }
      })
      .catch(() => {});
  }, []);

  const resolvedPair = contract === "all"
    ? undefined
    : (() => {
      const acc = rawAccounts.find(
        (a) => `${a.base_asset}_${a.quote_asset}`.toLowerCase() === contract
      );
      return acc ? `${acc.base_asset}${acc.quote_asset}` : undefined;
    })();

  const loadRows = useCallback(async (pair, from, to) => {
    setLoading(true);
    try {
      // Same call shape as margin trade page `reloadMarginPositionHistory`.
      const res = await AuthService.marginPositionHistory(pair || undefined, {
        page: 1,
        limit: 100,
        ...(from ? { start_date: from } : {}),
        ...(to ? { end_date: to } : {}),
      });
      if (res?.success) {
        const raw = Array.isArray(res.data)
          ? res.data
          : (Array.isArray(res.data?.items) ? res.data.items : []);
        setRows(raw.map(normalizeMarginPositionHistoryItem));
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows(resolvedPair, dateFrom, dateTo);
  }, [resolvedPair, dateFrom, dateTo, loadRows]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      if (!(e.target instanceof Element)) return;
      if (!e.target.closest("[data-so-dropdown]")) setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpenDropdown(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const filteredRows = rows.filter((pos) => {
    if (side === "all") return true;
    return String(pos.side || "").toUpperCase() === side.toUpperCase();
  });

  const { page, setPage, totalPages, pagedRows } = useOrderTablePagination(filteredRows);

  const resetFilters = () => {
    setSide("all");
    setContract("all");
    setDateFrom("");
    setDateTo("");
  };

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (el && typeof el.showPicker === "function") {
      try { el.showPicker(); return; } catch { /**/ }
    }
    el?.click();
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    const fromRef = keyPrefix === "mob" ? dateFromRefMob : dateFromRefDesk;
    const toRef = keyPrefix === "mob" ? dateToRefMob : dateToRefDesk;
    return (
      <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter margin position history">
        <div className="so-toolbar__left fullwidthdesktop">
          <CustomDropdown
            id={`${pref}mph-side`}
            label="Side"
            value={side}
            options={MARGIN_POSITION_SIDE_OPTIONS}
            onChange={setSide}
            menuKey={`${pref}mph-side`}
            openKey={openDropdown}
            onOpenToggle={setOpenDropdown}
          />
          <CustomDropdown
            id={`${pref}mph-contract`}
            label="Contract"
            value={contract}
            options={pairOptions}
            onChange={setContract}
            menuKey={`${pref}mph-contract`}
            openKey={openDropdown}
            onOpenToggle={setOpenDropdown}
          />
          <div className="so-dateBlock">
            <span className="so-dateBlock__label">Date</span>
            <div className="so-dateRow">
              <DatePickerCell value={dateFrom} onChange={setDateFrom} placeholder="From" ariaLabel="Start date" inputRef={fromRef} />
              <span className="so-dateArrow" aria-hidden="true">→</span>
              <DatePickerCell value={dateTo} onChange={setDateTo} placeholder="To" ariaLabel="End date" inputRef={toRef} />
              <button type="button" className="so-dateCalBtn" aria-label="Open calendar" onClick={() => openDatePicker(toRef)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.35" />
                  <path d="M3 10h18" stroke="currentColor" strokeWidth="1.35" />
                  <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          {loading
            ? <div className="so-emptyState__text">Loading…</div>
            : <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />}
        </div>
      </td>
    </tr>
  );

  const renderDesktopRow = (pos, idx) => {
    const rpnl = parseFloat(pos.realized_pnl || 0);
    const fees = parseFloat(pos.total_fees || 0);
    const qty = parseFloat(pos.quantity || 0);
    const notional = parseFloat(pos.notional || 0);
    const dt = fmtDt(pos.opened_at);
    const dtEnd = fmtDt(pos.closed_at);
    const pairLbl = formatPairLabel(pos.pair);
    const { base: baseAsset, quote: quoteAsset } = splitPairAssets(pos.pair);
    const isLong = pos.side === "LONG";
    const liqFeeN = parseFloat(pos.liq_fee);
    const showLiqFee = isLiquidatedPos(pos) && Number.isFinite(liqFeeN) && liqFeeN !== 0;
    const liqFeeAsset = pos.liq_fee_asset || quoteAsset || "";

    return (
      <tr key={pos.position_id || pos._id || idx}>
        <td>
          <div className="fw-semibold">{pairLbl}</div>
          <small className="text-muted">Isolated{pos.leverage ? ` · ${pos.leverage}x` : ""}</small>
        </td>
        <td>
          <span className={isLong ? "so-side--buy" : "so-side--sell"} style={{ fontWeight: 600 }}>
            {isLong ? "L" : "S"}
          </span>
        </td>
        <td>{posStatusLabel(pos)}</td>
        <td style={{ whiteSpace: "nowrap" }}>
          {dt ? <><div>{dt.date}</div><small style={{ color: "#9ca3af" }}>{dt.time}</small></> : "—"}
        </td>
        <td className="so-num">{fmt4(pos.entry_price)}{quoteAsset ? ` ${quoteAsset}` : ""}</td>
        <td className="so-num">{pos.close_price ? `${fmt4(pos.close_price)}${quoteAsset ? ` ${quoteAsset}` : ""}` : "—"}</td>
        <td className="so-num">{qty > 0 ? `${fmt4(qty)}${baseAsset ? ` ${baseAsset}` : ""}` : "—"}</td>
        <td className="so-num">{notional > 0 ? `${fmt4(notional)}${quoteAsset ? ` ${quoteAsset}` : ""}` : "—"}</td>
        <td className={`so-num ${rpnl >= 0 ? "so-side--buy" : "so-side--sell"}`}>
          {rpnl >= 0 ? "+" : ""}{fmt4(rpnl)}{quoteAsset ? ` ${quoteAsset}` : ""}
        </td>
        <td className="so-num">{fees !== 0 ? `${fmt4(fees)}${quoteAsset ? ` ${quoteAsset}` : ""}` : "—"}</td>
        <td className="so-num">{showLiqFee ? `${fmt4(liqFeeN)}${liqFeeAsset ? ` ${liqFeeAsset}` : ""}` : "—"}</td>
        <td style={{ whiteSpace: "nowrap" }}>
          {dtEnd ? <><div>{dtEnd.date}</div><small style={{ color: "#9ca3af" }}>{dtEnd.time}</small></> : "—"}
        </td>
      </tr>
    );
  };

  const renderMobileCard = (pos, idx) => {
    const rpnl = parseFloat(pos.realized_pnl || 0);
    const fees = parseFloat(pos.total_fees || 0);
    const qty = parseFloat(pos.quantity || 0);
    const notional = parseFloat(pos.notional || 0);
    const dt = fmtDt(pos.opened_at);
    const dtEnd = fmtDt(pos.closed_at);
    const pairLbl = formatPairLabel(pos.pair);
    const { base: baseAsset, quote: quoteAsset } = splitPairAssets(pos.pair);
    const isLong = pos.side === "LONG";
    const liqFeeN = parseFloat(pos.liq_fee);
    const showLiqFee = isLiquidatedPos(pos) && Number.isFinite(liqFeeN) && liqFeeN !== 0;
    const liqFeeAsset = pos.liq_fee_asset || quoteAsset || "";

    return (
      <OrderMobileCardShell
        key={`m-${pos.position_id || pos._id || idx}`}
        desktopColumnCount={ORDER_DESKTOP_COL_COUNT}
        popupTitle="Position details"
      >
        <div className="so-mobileCard__row"><span>Spot/Leverage</span><span>{pairLbl} · Isolated{pos.leverage ? ` · ${pos.leverage}x` : ""}</span></div>
        <div className="so-mobileCard__row"><span>Side</span><span className={isLong ? "so-side--buy" : "so-side--sell"}>{isLong ? "L" : "S"}</span></div>
        <div className="so-mobileCard__row"><span>Status</span><span>{posStatusLabel(pos)}</span></div>
        <div className="so-mobileCard__row"><span>Entry Time</span><span>{dt ? `${dt.date} ${dt.time}` : "—"}</span></div>
        <div className="so-mobileCard__row"><span>Close-All Time</span><span>{dtEnd ? `${dtEnd.date} ${dtEnd.time}` : "—"}</span></div>
        <div className="so-mobileCard__row"><span>Entry Price</span><span className="so-num">{fmt4(pos.entry_price)}{quoteAsset ? ` ${quoteAsset}` : ""}</span></div>
        <div className="so-mobileCard__row"><span>Exit Price</span><span className="so-num">{pos.close_price ? `${fmt4(pos.close_price)}${quoteAsset ? ` ${quoteAsset}` : ""}` : "—"}</span></div>
        <div className="so-mobileCard__row"><span>Peak Position</span><span className="so-num">{qty > 0 ? `${fmt4(qty)}${baseAsset ? ` ${baseAsset}` : ""}` : "—"}</span></div>
        <div className="so-mobileCard__row"><span>Notional</span><span className="so-num">{notional > 0 ? `${fmt4(notional)}${quoteAsset ? ` ${quoteAsset}` : ""}` : "—"}</span></div>
        <div className="so-mobileCard__row">
          <span>Realized PnL</span>
          <span className={`so-num ${rpnl >= 0 ? "so-side--buy" : "so-side--sell"}`}>
            {rpnl >= 0 ? "+" : ""}{fmt4(rpnl)}{quoteAsset ? ` ${quoteAsset}` : ""}
          </span>
        </div>
        <div className="so-mobileCard__row"><span>Fees</span><span className="so-num">{fees !== 0 ? `${fmt4(fees)}${quoteAsset ? ` ${quoteAsset}` : ""}` : "—"}</span></div>
        {showLiqFee ? (
          <div className="so-mobileCard__row"><span>Liq. Fee</span><span className="so-num">{fmt4(liqFeeN)}{liqFeeAsset ? ` ${liqFeeAsset}` : ""}</span></div>
        ) : null}
      </OrderMobileCardShell>
    );
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="mph-title">
            <h1 id="mph-title" className="so-page__title">Position History</h1>
            <OrdersSubTabs tabs={MARGIN_SUB_TABS} />
            {renderToolbar(false, "desk")}
            <div className="so-tableWrap">
              <table className="so-table so-table--rowLines so-table--positionHistory">
                <thead>
                  <tr>
                    <th scope="col">Spot/Leverage</th>
                    <th scope="col">Side</th>
                    <th scope="col">Status</th>
                    <th scope="col">Entry Time</th>
                    <th scope="col">Entry Price</th>
                    <th scope="col">Exit Price</th>
                    <th scope="col">Peak Position</th>
                    <th scope="col">Notional</th>
                    <th scope="col">Realized PnL</th>
                    <th scope="col">Fees</th>
                    <th scope="col">Liq. Fee</th>
                    <th scope="col">Close-All Time</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? renderEmptyBody() : pagedRows.map(renderDesktopRow)}
                </tbody>
              </table>
            </div>

            <OrderTablePagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              label="Position history pages"
            />
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Position History</h2>
          <OrdersSubTabs tabs={MARGIN_SUB_TABS} />
          <div className="so-mobile__toolbar">
            <SoMobileFilters modalId="mob-mph-filters" toolbar={renderToolbar(true, "mob")} onReset={resetFilters} />
          </div>
          {filteredRows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              {loading
                ? <div className="so-emptyState__text">Loading…</div>
                : <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />}
            </div>
          ) : pagedRows.map(renderMobileCard)}

          <OrderTablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            label="Position history pages"
          />
        </div>
      </div>
    </div>
  );
};

export default MarginPositionHistory;
